import { useEffect, useState } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';

/**
 * Detects whether the last image of the feed is a panoramic (360 spin) image.
 *
 * Matches the detection used by ws-vehicle-media: the image is considered
 * panoramic when its natural width is at least twice its natural height.
 * Detection requires the natural dimensions of the image, so it only runs in
 * the browser.
 *
 * @param {Object[]} images The full, unfiltered list of gallery images
 * @param {boolean} enabled Whether 360 support is enabled (show360Image preference)
 * @returns {Object|null} The panoramic image, or null when there isn't one
 */
export default function usePanoramicImage(images = [], enabled = false) {
	const [panoramicImage, setPanoramicImage] = useState(null);

	useEffect(() => {
		const lastImage = images[images.length - 1];

		if (!isBrowser || !enabled || !lastImage?.src) {
			setPanoramicImage(null);
			return undefined;
		}

		// Guards against a slow response for a previous image list resolving
		// after the list has already changed.
		let isStale = false;
		const probe = new Image();

		probe.onload = () => {
			if (isStale) {
				return;
			}
			const { naturalWidth: width, naturalHeight: height } = probe;
			const isPanoramic = height > 0 && width / 2 >= height;
			setPanoramicImage(isPanoramic ? lastImage : null);
		};

		probe.onerror = () => {
			if (!isStale) {
				setPanoramicImage(null);
			}
		};

		probe.src = lastImage.src;

		return () => {
			isStale = true;
			probe.onload = null;
			probe.onerror = null;
		};
	}, [enabled, images]);

	return panoramicImage;
}
